import { resolveSurfacePlatform, type SurfacePlatform } from "@ui/hooks/platform";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { usePlatform } from "@ui/hooks/use-platform";
import {
  resolveSheetDismissal,
  Sheet,
  type SheetRootProps,
  type SheetTrack,
  sheetDismissalProps,
  useSheetMotion,
  useSheetPresentation,
} from "@ui/primitives/Sheet";
import { type ReactNode, use, useMemo, useRef, useState } from "react";
import {
  SheetSurfaceContext,
  SheetSurfaceInternalContext,
  type SheetSurfaceValue,
  useSheetSurface,
} from "./SheetSurfaceContext";
import {
  type SurfaceChrome,
  type SurfaceSize,
  surfaceContent,
  surfacePanel,
  surfaceView,
} from "./sheet-surface.styles";

export interface SheetSurfaceRootProps {
  children: ReactNode;
  /** Controlled open state. Omit for uncontrolled/trigger-driven use. */
  isOpen?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** ARIA role for the surface. "alertdialog" hardens scrim tap and swipe. */
  role?: "dialog" | "alertdialog";
  size?: SurfaceSize;
  /** "bare" drops the fill and shadow so the content supplies its own surface. */
  chrome?: SurfaceChrome;
  /** Master dismissal gate: scrim tap, Escape, close control, and swipe. */
  dismissible?: boolean;
  /** Set `false` to keep Escape from dismissing while other routes stay open. */
  keyboardDismissible?: boolean;
  /** Mobile only: a drag grabber replaces the header's nav control. */
  grabber?: boolean;
  /** Fires once after the exit animation completes. */
  onExitComplete?: () => void;
  /** Joins this surface to a `SheetStack`, so its travel drives that stack outlet. */
  stack?: SheetRootProps["forComponent"];
  /** @internal Storybook/testing — forces the resolved platform. */
  platformOverride?: SurfacePlatform;
}

/**
 * The house traveling surface: one Silk sheet, one presentation lifecycle, one
 * dismissal policy. `Dialog`, `Page` and `MobileSheet` are configurations of
 * it — they choose placement, track and skin, and nothing else.
 *
 * Every Silk workaround the surfaces need lives in `View` below rather than in
 * a prop, so a new surface cannot forget one.
 */
function SheetSurfaceRoot({
  children,
  isOpen,
  defaultOpen,
  onOpenChange,
  onExitComplete,
  role = "dialog",
  size = "sm",
  chrome = "card",
  dismissible = true,
  keyboardDismissible = true,
  grabber = false,
  stack,
  platformOverride,
}: SheetSurfaceRootProps) {
  // `isOpen` stops here: it is Silk's word, and the seam is where it ends.
  const presentation = useSheetPresentation({ defaultOpen, isOpen, onExitComplete, onOpenChange });
  const { close, mounted } = presentation;

  // The platform is latched while the surface is mounted: crossing the `md`
  // breakpoint mid-session (e.g. phone rotation) must not swap the skin and
  // placement mid-animation.
  const isDesktop = useBreakpoint("md");
  const os = usePlatform();
  const livePlatform = resolveSurfacePlatform({ isDesktop, os, override: platformOverride });
  const latchedPlatform = useRef(livePlatform);
  if (!mounted) latchedPlatform.current = livePlatform;
  const platform = latchedPlatform.current;

  const dismissal = useMemo(
    () => resolveSheetDismissal({ dismissible, keyboard: keyboardDismissible }, role),
    [dismissible, keyboardDismissible, role],
  );

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasFooter, setHasFooter] = useState(false);

  const value = useMemo<SheetSurfaceValue>(
    () => ({
      chrome,
      close,
      dismissal,
      grabber,
      hasFooter,
      isAtBottom,
      platform,
      setHasFooter,
      setIsAtBottom,
      size,
    }),
    [chrome, close, dismissal, grabber, hasFooter, isAtBottom, platform, size],
  );

  const internal = useMemo(() => ({ mounted, presentation }), [mounted, presentation]);

  return (
    <Sheet.Root className="contents" forComponent={stack} sheetRole={role} {...presentation.rootProps}>
      <SheetSurfaceContext value={value}>
        <SheetSurfaceInternalContext value={internal}>{children}</SheetSurfaceInternalContext>
      </SheetSurfaceContext>
    </Sheet.Root>
  );
}

export interface SheetSurfaceViewProps {
  children: ReactNode;
  /** Accessible name. Omit only when the surface renders a `Sheet.Title`. */
  label?: string;
  /** Where the panel rests. */
  placement: "bottom" | "center" | "right";
  /** Travel axis. Defaults to the placement's natural axis. */
  track?: Extract<SheetTrack, "bottom" | "right">;
  /** Whether the panel fills the travel container (fullscreen) or sizes to content. */
  fill?: boolean;
  /** Whether a swipe gesture travels the panel. */
  swipe?: boolean;
  /** Scrim classes. */
  backdropClassName: string;
  /** Panel skin classes, from the recipe's own styles. */
  className?: string;
}

/**
 * The overlay subtree: portal, travel view, backdrop, and the panel. The only
 * place in the repo that composes Silk's sheet anatomy.
 *
 * Bakes in, rather than exposing as props, every constraint a surface must not
 * get wrong: the accessible-name plumbing, the backdrop travel range, the
 * dismissal wiring, `data-travel-status` for tests, and the inner panel
 * element that exists because Silk's `!important` styles leave `Sheet.Content`
 * unable to clip or centre anything.
 */
function SheetSurfaceView({
  children,
  label,
  placement,
  track,
  fill = false,
  swipe,
  backdropClassName,
  className,
}: SheetSurfaceViewProps) {
  const { dismissal } = useSheetSurface();
  const internal = use(SheetSurfaceInternalContext);
  const motion = useSheetMotion();
  if (!internal) throw new Error("SheetSurface.View must be rendered inside SheetSurface.Root");
  if (!internal.mounted) return null;

  const { presentation } = internal;

  return (
    <Sheet.Portal>
      <Sheet.View
        // Silk always emits `aria-describedby`, pointing at a Description that
        // most surfaces do not render — a dangling reference on every one.
        aria-describedby={undefined}
        aria-label={label}
        className={surfaceView()}
        contentPlacement={placement}
        enteringAnimationSettings={motion.entering}
        exitingAnimationSettings={motion.exiting}
        // Keeps the OS back/forward edge gesture from firing over the surface.
        nativeEdgeSwipePrevention={placement === "bottom"}
        swipe={swipe}
        tracks={track}
        {...sheetDismissalProps(dismissal, presentation.close)}
        {...presentation.viewProps}
      >
        {/* No themeColorDimming: Silk crashes on iOS UAs when a dimming overlay
            registers before its underlying theme color is initialized, which is
            the case for a lazily-mounted View. Status-bar dimming is a follow-up. */}
        <Sheet.Backdrop className={backdropClassName} travelAnimation={motion.backdropTravel} />
        <Sheet.Content className={surfaceContent({ fill })}>
          <div className={surfacePanel({ class: className })}>{children}</div>
        </Sheet.Content>
      </Sheet.View>
    </Sheet.Portal>
  );
}

export const SheetSurface = {
  Root: SheetSurfaceRoot,
  Trigger: Sheet.Trigger,
  View: SheetSurfaceView,
};
